import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { KeyRound, Mail, Database, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const initial = {
  openai_api_key: "", ai_model: "gpt-5.4-mini",
  resend_api_key: "", sender_email: "", sender_name: "AZHD NutriTrack",
  usda_api_key: "",
};

function Status({ configured, masked }) {
  return configured
    ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Configured {masked ? `· ${masked}` : ""}</Badge>
    : <Badge variant="secondary">Not configured</Badge>;
}

export default function AdminIntegrations() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState({});
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({});
  const set = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const load = async () => {
    try {
      const { data } = await api.get("/admin/integrations");
      setStatus(data);
      setForm((old) => ({
        ...old,
        ai_model: data.openai?.model || "gpt-5.4-mini",
        sender_email: data.resend?.sender_email || "",
        sender_name: data.resend?.sender_name || "AZHD NutriTrack",
      }));
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((key) => {
        if (typeof payload[key] === "string") payload[key] = payload[key].trim();
      });
      const { data } = await api.put("/admin/integrations", payload);
      setStatus(data);
      setForm((old) => ({ ...old, openai_api_key: "", resend_api_key: "", usda_api_key: "" }));
      toast.success("Integration settings saved securely");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const SecretInput = ({ id, label, value, onChange, placeholder }) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input id={id} type={show[id] ? "text" : "password"} value={value} onChange={onChange}
          placeholder={placeholder} autoComplete="new-password" className="pr-11 font-mono" data-testid={id} />
        <button type="button" onClick={() => setShow((old) => ({ ...old, [id]: !old[id] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label={`Toggle ${label}`}>
          {show[id] ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      <p className="text-xs text-muted-foreground">Leave blank to keep the currently saved key.</p>
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-integrations-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Super admin</p>
        <h1 className="text-3xl font-bold mt-1">Integrations</h1>
        <p className="text-muted-foreground mt-1">Manage external services used by AI plans, email delivery, and food search.</p>
      </header>

      <section className="nt-card p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3"><KeyRound className="text-primary mt-0.5" size={20} /><div><h2 className="font-semibold text-lg">OpenAI</h2><p className="text-sm text-muted-foreground">Generates draft diet plans through the Responses API.</p></div></div>
          <Status configured={status.openai?.configured} masked={status.openai?.masked_key} />
        </div>
        <SecretInput id="openai-api-key" label="OpenAI API key" value={form.openai_api_key} onChange={(e) => set("openai_api_key", e.target.value)} placeholder="sk-..." />
        <div className="space-y-2"><Label htmlFor="ai-model">Model</Label><Input id="ai-model" value={form.ai_model} onChange={(e) => set("ai_model", e.target.value)} data-testid="ai-model" /><p className="text-xs text-muted-foreground">Use a model available to your OpenAI project.</p></div>
      </section>

      <section className="nt-card p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3"><Mail className="text-primary mt-0.5" size={20} /><div><h2 className="font-semibold text-lg">Resend email</h2><p className="text-sm text-muted-foreground">Sends OTPs, password resets, and patient notifications.</p></div></div>
          <Status configured={status.resend?.configured} masked={status.resend?.masked_key} />
        </div>
        <SecretInput id="resend-api-key" label="Resend API key" value={form.resend_api_key} onChange={(e) => set("resend_api_key", e.target.value)} placeholder="re_..." />
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2"><Label htmlFor="sender-email">Verified sender email</Label><Input id="sender-email" type="email" value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)} data-testid="sender-email" /></div>
          <div className="space-y-2"><Label htmlFor="sender-name">Sender name</Label><Input id="sender-name" value={form.sender_name} onChange={(e) => set("sender_name", e.target.value)} data-testid="sender-name" /></div>
        </div>
      </section>

      <section className="nt-card p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3"><Database className="text-primary mt-0.5" size={20} /><div><h2 className="font-semibold text-lg">USDA FoodData Central</h2><p className="text-sm text-muted-foreground">Imports verified food and nutrition information.</p></div></div>
          <Status configured={status.usda?.configured} masked={status.usda?.masked_key} />
        </div>
        <SecretInput id="usda-api-key" label="USDA API key" value={form.usda_api_key} onChange={(e) => set("usda_api_key", e.target.value)} placeholder="API key" />
      </section>

      <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-integrations">
        <Save size={16} /> {saving ? "Saving…" : "Save integrations"}
      </Button>
      <p className="text-xs text-muted-foreground">Secret keys are encrypted before database storage and are never returned by the API.</p>
    </div>
  );
}
